import { useEffect } from 'react';

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $createImageNode,
  $isImageNode,
  addComposerChild$,
  addExportVisitor$,
  addImportVisitor$,
  ImageNode,
  isMdastJsxNode,
  realmPlugin,
  type LexicalVisitor,
  type MdastHTMLNode,
  type MdastImportVisitor,
} from '@mdxeditor/editor';
import {
  $getNodeByKey,
  $isElementNode,
  COMMAND_PRIORITY_EDITOR,
  createCommand,
  type LexicalNode,
} from 'lexical';
import {
  type MdxJsxAttribute,
  type MdxJsxExpressionAttribute,
} from 'mdast-util-mdx-jsx';

const FLOAT_CLASSES = ['mdx-float-left', 'mdx-float-right'] as const;

export type ImageFloat = 'left' | 'right' | 'none';

type ImageFloatCommandPayload = {
  nodeKey: string;
  value: ImageFloat;
};

type MdxAttributeWithName = {
  type: 'mdxJsxAttribute';
  name: string;
  value?: unknown;
};

const FLOAT_STYLE_REGEX = /(^|;)\s*float\s*:\s*(left|right)\s*(;|$)/i;
const FLOAT_CLASS_LEFT = 'mdx-float-left';
const FLOAT_CLASS_RIGHT = 'mdx-float-right';

export const FLOAT_IMAGE_COMMAND = createCommand<ImageFloatCommandPayload>(
  'FLOAT_IMAGE_COMMAND',
);

const isNamedMdxAttribute = (value: unknown): value is MdxAttributeWithName =>
  typeof value === 'object' &&
  value !== null &&
  'type' in value &&
  (value as { type?: unknown }).type === 'mdxJsxAttribute' &&
  'name' in value &&
  typeof (value as { name?: unknown }).name === 'string';

const getStyleValueFromAttributes = (attributes: unknown[]) => {
  const styleAttribute = attributes?.find(
    (attr) => isNamedMdxAttribute(attr) && attr.name === 'style',
  );

  return isNamedMdxAttribute(styleAttribute) &&
    typeof styleAttribute.value === 'string'
    ? styleAttribute.value
    : '';
};

const getClassValueFromAttributes = (attributes: unknown[]) => {
  const classAttribute = attributes.find(
    (attr) =>
      isNamedMdxAttribute(attr) &&
      (attr.name === 'class' || attr.name === 'className'),
  );

  return isNamedMdxAttribute(classAttribute) &&
    typeof classAttribute.value === 'string'
    ? classAttribute.value
    : '';
};

const getImageFloatFromStyle = (style: string): ImageFloat => {
  const floatMatch = style.match(FLOAT_STYLE_REGEX);

  return floatMatch
    ? (floatMatch[2].toLowerCase() as 'left' | 'right')
    : 'none';
};

export const getImageFloatFromNode = (node: LexicalNode): ImageFloat => {
  if (!$isImageNode(node)) {
    return 'none';
  }
  const styleFloat = getImageFloatFromStyle(
    getStyleValueFromAttributes(node.getRest()),
  );

  if (styleFloat !== 'none') {
    return styleFloat;
  }

  const className = getClassValueFromAttributes(node.getRest());

  if (className.split(/\s+/).includes(FLOAT_CLASS_LEFT)) {
    return 'left';
  }
  if (className.split(/\s+/).includes(FLOAT_CLASS_RIGHT)) {
    return 'right';
  }

  return 'none';
};

const buildStyleWithFloat = (style: string, value: ImageFloat) => {
  const styleWithoutFloat = style
    .split(';')
    .map((part) => part.trim())
    .filter(
      (part) => part.length > 0 && !part.toLowerCase().startsWith('float:'),
    )
    .join(';');

  if (value === 'none') {
    return styleWithoutFloat ? `${styleWithoutFloat};` : '';
  }

  return styleWithoutFloat
    ? `${styleWithoutFloat}; float: ${value};`
    : `float: ${value};`;
};

const buildClassWithFloat = (className: string, value: ImageFloat) => {
  const classList = className
    .split(/\s+/)
    .map((part) => part.trim())
    .filter(
      (part) =>
        part.length > 0 &&
        part !== FLOAT_CLASS_LEFT &&
        part !== FLOAT_CLASS_RIGHT,
    );

  if (value === 'left') {
    classList.push(FLOAT_CLASS_LEFT);
  } else if (value === 'right') {
    classList.push(FLOAT_CLASS_RIGHT);
  }

  return classList.join(' ');
};

const buildRestWithFloat = (
  rest: (MdxJsxAttribute | MdxJsxExpressionAttribute)[],
  value: ImageFloat,
) => {
  const restWithoutStyleAndClass = rest.filter(
    (attr) =>
      !(
        isNamedMdxAttribute(attr) &&
        (attr.name === 'style' ||
          attr.name === 'class' ||
          attr.name === 'className')
      ),
  );
  const nextStyle = buildStyleWithFloat(
    getStyleValueFromAttributes(rest),
    value,
  );
  const nextClassName = buildClassWithFloat(
    getClassValueFromAttributes(rest),
    value,
  );
  const nextRest = [...restWithoutStyleAndClass];

  if (nextStyle) {
    nextRest.push({
      type: 'mdxJsxAttribute' as const,
      name: 'style',
      value: nextStyle,
    });
  }
  if (nextClassName) {
    nextRest.push({
      type: 'mdxJsxAttribute' as const,
      name: 'className',
      value: nextClassName,
    });
  }

  return nextRest as (MdxJsxAttribute | MdxJsxExpressionAttribute)[];
};

const exportImageFloatVisitor: LexicalVisitor = {
  testLexicalNode: $isImageNode,
  priority: 1,
  visitLexicalNode({ lexicalNode, actions }) {
    if (!$isImageNode(lexicalNode)) {
      actions.nextVisitor();
      return;
    }

    const floatValue = getImageFloatFromNode(lexicalNode);

    if (floatValue === 'none') {
      actions.nextVisitor();
      return;
    }

    const normalizedRest = buildRestWithFloat(
      lexicalNode.getRest() as (MdxJsxAttribute | MdxJsxExpressionAttribute)[],
      floatValue,
    );

    const restAttributes = normalizedRest.filter(
      (attr) =>
        isNamedMdxAttribute(attr) &&
        !['src', 'alt', 'title', 'width', 'height'].includes(
          attr.name.toLowerCase(),
        ),
    ) as MdxAttributeWithName[];

    const attributes: MdxAttributeWithName[] = [
      { type: 'mdxJsxAttribute', name: 'src', value: lexicalNode.getSrc() },
      ...restAttributes,
    ];
    if (lexicalNode.getAltText()) {
      attributes.push({
        type: 'mdxJsxAttribute',
        name: 'alt',
        value: lexicalNode.getAltText(),
      });
    }

    if (lexicalNode.getTitle()) {
      attributes.push({
        type: 'mdxJsxAttribute',
        name: 'title',
        value: lexicalNode.getTitle(),
      });
    }

    const width = lexicalNode.getWidth();
    if (typeof width === 'number') {
      attributes.push({
        type: 'mdxJsxAttribute',
        name: 'width',
        value: String(width),
      });
    }

    const height = lexicalNode.getHeight();
    if (typeof height === 'number') {
      attributes.push({
        type: 'mdxJsxAttribute',
        name: 'height',
        value: String(height),
      });
    }

    actions.addAndStepInto('mdxJsxFlowElement', {
      type: 'mdxJsxFlowElement',
      name: 'img',
      attributes,
    });
  },
};

const importImageFloatVisitor: MdastImportVisitor<MdastHTMLNode> = {
  priority: 1,
  testNode: (mdastNode) =>
    isMdastJsxNode(mdastNode) &&
    mdastNode.name === 'img' &&
    FLOAT_STYLE_REGEX.test(getStyleValueFromAttributes(mdastNode?.attributes)),
  visitNode({ mdastNode, lexicalParent, actions }) {
    if (!isMdastJsxNode(mdastNode) || !$isElementNode(lexicalParent)) {
      actions.nextVisitor();
      return;
    }
    const srcAttribute = mdastNode.attributes.find(
      (attr) => isNamedMdxAttribute(attr) && attr.name === 'src',
    );
    const src =
      isNamedMdxAttribute(srcAttribute) &&
      typeof srcAttribute.value === 'string'
        ? srcAttribute.value
        : '';

    if (!src) {
      actions.nextVisitor();
      return;
    }

    const altAttribute = mdastNode.attributes.find(
      (attr) => isNamedMdxAttribute(attr) && attr.name === 'alt',
    );
    const titleAttribute = mdastNode.attributes.find(
      (attr) => isNamedMdxAttribute(attr) && attr.name === 'title',
    );
    const widthAttribute = mdastNode.attributes.find(
      (attr) => isNamedMdxAttribute(attr) && attr.name === 'width',
    );
    const heightAttribute = mdastNode.attributes.find(
      (attr) => isNamedMdxAttribute(attr) && attr.name === 'height',
    );

    lexicalParent.append(
      $createImageNode({
        src,
        altText:
          isNamedMdxAttribute(altAttribute) &&
          typeof altAttribute.value === 'string'
            ? altAttribute.value
            : '',
        title:
          isNamedMdxAttribute(titleAttribute) &&
          typeof titleAttribute.value === 'string'
            ? titleAttribute.value
            : undefined,
        width:
          isNamedMdxAttribute(widthAttribute) &&
          typeof widthAttribute.value === 'string' &&
          Number.isFinite(Number(widthAttribute.value))
            ? Number(widthAttribute.value)
            : undefined,
        height:
          isNamedMdxAttribute(heightAttribute) &&
          typeof heightAttribute.value === 'string' &&
          Number.isFinite(Number(heightAttribute.value))
            ? Number(heightAttribute.value)
            : undefined,
        rest: mdastNode.attributes as (
          | MdxJsxAttribute
          | MdxJsxExpressionAttribute
        )[],
      }),
    );
  },
};

const ImageFloatCommandHandler = () => {
  const [editor] = useLexicalComposerContext();

  useEffect(
    () =>
      editor.registerCommand(
        FLOAT_IMAGE_COMMAND,
        ({ nodeKey, value }) => {
          const imageNode = $getNodeByKey(nodeKey);

          if (!$isImageNode(imageNode)) {
            return false;
          }

          const width = imageNode.getWidth();
          const height = imageNode.getHeight();

          const updatedNode = $createImageNode({
            src: imageNode.getSrc(),
            altText: imageNode.getAltText(),
            title: imageNode.getTitle(),
            width: typeof width === 'number' ? width : undefined,
            height: typeof height === 'number' ? height : undefined,
            rest: buildRestWithFloat(
              imageNode.getRest() as (
                | MdxJsxAttribute
                | MdxJsxExpressionAttribute
              )[],
              value,
            ),
          });

          imageNode.replace(updatedNode);

          return true;
        },
        COMMAND_PRIORITY_EDITOR,
      ),
    [editor],
  );

  return null;
};

export const ImageFloatParentSync = () => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerMutationListener(
      ImageNode,
      (mutatedNodes) => {
        editor.getEditorState().read(() => {
          for (const [nodeKey] of mutatedNodes) {
            const node = $getNodeByKey(nodeKey);

            if (!$isImageNode(node)) {
              continue;
            }

            const imageElement = editor.getElementByKey(nodeKey);

            if (!imageElement) {
              continue;
            }

            imageElement.classList.remove(...FLOAT_CLASSES);

            const floatValue = getImageFloatFromNode(node);

            if (floatValue === 'left') {
              imageElement.classList.add('mdx-float-left');
            }

            if (floatValue === 'right') {
              imageElement.classList.add('mdx-float-right');
            }
          }
        });
      },
      {
        skipInitialization: false,
      },
    );
  }, [editor]);

  return null;
};

export const imageFloatPlugin = realmPlugin({
  init(realm) {
    realm.pub(addExportVisitor$, exportImageFloatVisitor);
    realm.pub(addImportVisitor$, importImageFloatVisitor);
    realm.pub(addComposerChild$, ImageFloatCommandHandler);
    realm.pub(addComposerChild$, ImageFloatParentSync);
  },
});
